// ---------------------------------------------------------------------------
// useVariableRows — the shared variable-rows state model for grader forms. Both
// the create spine (`useGraderCreateBase`) and the edit flow (`useEditGrader`)
// hold the same list of editable `VariableRow`s, validate it the same way, and
// mutate it with the same add / update / declare / remove handlers. This hook
// owns that model so the two callers stay in sync (notably `declareVariable`,
// the guided one-click declaration behind the template-var callouts).
// ---------------------------------------------------------------------------

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { VariableRow } from '../AgentsPage/AgentForm/AgentForm.types.js';
import { emptyVariableRow } from '../AgentsPage/AgentForm/AgentForm.utils.js';

import { validateVariableRows } from './graderVariables.utils.js';

export interface UseVariableRowsResult {
  variables: VariableRow[];
  /** Escape hatch for bulk resets (e.g. loading a grader into edit mode). */
  setVariables: Dispatch<SetStateAction<VariableRow[]>>;
  /** Per-row validation errors keyed by row id; empty when all rows are valid. */
  variableErrors: Record<string, string>;
  updateVariable: (id: string, patch: Partial<VariableRow>) => void;
  addVariable: () => void;
  /** Append a variable row pre-filled with `name` (for guided declaration). */
  declareVariable: (name: string) => void;
  removeVariable: (id: string) => void;
}

export function useVariableRows(
  initial: VariableRow[] = [],
): UseVariableRowsResult {
  const [variables, setVariables] = useState<VariableRow[]>(initial);

  const variableErrors = useMemo(
    () => validateVariableRows(variables),
    [variables],
  );

  const updateVariable = useCallback(
    (id: string, patch: Partial<VariableRow>) => {
      setVariables((current) =>
        current.map((variable) =>
          variable.id === id ? { ...variable, ...patch } : variable,
        ),
      );
    },
    [],
  );
  const addVariable = useCallback(() => {
    setVariables((current) => [...current, emptyVariableRow()]);
  }, []);
  const declareVariable = useCallback((name: string) => {
    setVariables((current) => [...current, { ...emptyVariableRow(), name }]);
  }, []);
  const removeVariable = useCallback((id: string) => {
    setVariables((current) => current.filter((variable) => variable.id !== id));
  }, []);

  return {
    variables,
    setVariables,
    variableErrors,
    updateVariable,
    addVariable,
    declareVariable,
    removeVariable,
  };
}
